import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Layout from "./components/ui/Layout";
import LeaveReview from "./pages/LeaveReview";
import { useEffect, useState } from "react";
import apiClient from "./api/apiClient";
import axios from "axios"; // ✅ ADD THIS LINE

import Index from "./pages/Index";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import SetPassword from "./pages/SetPassword";
import Dashboard from "./pages/Dashboard";
import Pricing from "./pages/Pricing";
import Subscription from "./pages/Subscription";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCanceled from "./pages/PaymentCanceled";
import EmailVerificationPage from "./pages/VerifyEmail";
import NotFound from "./pages/NotFound";
import MicTest from "./pages/MicTest";
import MicTest2 from "./pages/MicTest2";
import MicTest3 from "./pages/MicTest3";
import TaskOne from "./pages/TaskOne";
import TaskThree from "./pages/TaskThree";
import LevelOption from "./pages/LevelOption";
import ExamComplete from "./pages/ExamComplete";
import TaskTwo from "./pages/TaskTwo";
import ExamResults from "./pages/ExamResults";
import { AuthProvider } from "./contaxt/AuthContext";
import ExamList from "./pages/ExamList";
import ProtectedRoute from "./components/ProtectedRoute";
import ExamDetails from "./pages/ExamDetails";
import PublicRoute from "./components/PublicRoute";
import SingleTaskFeedback from "./pages/SingleTaskFeedback";
import Task1ProcessingScreen from "./pages/Task1ProcessingScreen";
import Task2ProcessingScreen from "./pages/Task2ProcessingScreen";
import Task3ProcessingScreen from "./pages/Task3ProcessingScreen";
import Feedback from "./pages/Feedback";
import CombinedFeedbackProcessing from "./pages/CombinedFeedbackProcessing";
import ComprehensiveFeedbackResults from "./pages/ComprehensiveFeedbackResults";
import PriceIframe from "./pages/priceIframe";
import Templates from "./pages/ExploreTemplates";
import Footer from "./components/Footer";
import { getExamId, getGuestExamUser, getSessionId, getUserDataEmail, getUserDataId } from "./utils/storage";

const queryClient = new QueryClient();

import ScrollToTop from "./components/ScrollToTop";

const App = () => {
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [reportText, setReportText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSendReport = async () => {
    if (!reportText.trim()) return;

    try {
      setIsSending(true);

      // ✅ Use axios directly for Vercel API routes (same as review email)
      const guestUser = getGuestExamUser();
      const userId = getUserDataId() || guestUser?.user_id || null;
      const userEmail = getUserDataEmail() || guestUser?.email || null;
      const examId =
        localStorage.getItem("current_exam_id") ||
        getSessionId() ||
        getExamId() ||
        null;

      // ✅ Use axios directly for Vercel API routes (same as review email)
      await axios.post("/api/report_issue", {
        message: reportText,
        userId,
        userEmail,
        examId,
        page: window.location.pathname,
        userAgent: navigator.userAgent,
      });

      setIsSent(true);
      setReportText("");
    } catch (err) {
      console.error("Failed to send report", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  // ✅ effect only for event wiring
  useEffect(() => {
    const openModal = () => {
      setIsSent(false);
      setReportText("");
      setShowHelpModal(true);
    };
    window.addEventListener("open-help-report-modal", openModal);

    return () => {
      window.removeEventListener("open-help-report-modal", openModal);
    };
  }, []);

    // ✅ ESC key closes Help & Support modal
  useEffect(() => {
    if (!showHelpModal) return;
  
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowHelpModal(false);
      }
    };
  
    document.addEventListener("keydown", handleKeyDown);
  
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showHelpModal]);


  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <Routes>
              {/* Routes without navbar */}
              <Route path="/plans" element={<PriceIframe />} />

              {/* Routes with navbar (Layout) */}
              <Route
                path="/*"
                element={
                  <Layout>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <PublicRoute>
                            <Login />
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/login"
                        element={
                          <PublicRoute>
                            <Login />
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/forgot-password"
                        element={
                          <PublicRoute>
                            <ForgotPassword />
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/set-password"
                        element={
                          <PublicRoute>
                            <SetPassword />
                          </PublicRoute>
                        }
                      />

                      {/* Protected routes */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/pricing"
                        element={
                          <ProtectedRoute>
                            <Pricing />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/subscription"
                        element={
                          <ProtectedRoute>
                            <Subscription />
                          </ProtectedRoute>
                        }
                      />

                      <Route path="/payment-success" element={<PaymentSuccess />} />
                      <Route path="/payment-canceled" element={<PaymentCanceled />} />
                      <Route path="/verify-email" element={<EmailVerificationPage />} />

                      <Route path="/mic-test" element={<MicTest />} />
                      <Route path="/mic-test2" element={<MicTest2 />} />
                      <Route path="/mic-test3" element={<MicTest3 />} />

                      <Route path="/task-one" element={<TaskOne />} />
                      <Route path="/task-two" element={<TaskTwo />} />
                      <Route path="/task-three" element={<TaskThree />} />

                      <Route
                        path="/level-option"
                        element={
                          <ProtectedRoute>
                            <LevelOption />
                          </ProtectedRoute>
                        }
                      />

                      <Route path="/exam-complete" element={<ExamComplete />} />
                      <Route path="/exam-results" element={<ExamResults />} />

                      <Route path="/single-task-feedback" element={<SingleTaskFeedback />} />

                      <Route path="/task1-processing" element={<Task1ProcessingScreen />} />
                      <Route path="/task2-processing" element={<Task2ProcessingScreen />} />
                      <Route path="/task3-processing" element={<Task3ProcessingScreen />} />

                      <Route path="/feedback" element={<Feedback />} />
                      <Route path="/combined-feedback-processing" element={<CombinedFeedbackProcessing />} />
                      <Route path="/comprehensive-feedback-results" element={<ComprehensiveFeedbackResults />} />

                      <Route
                        path="/exam-list"
                        element={
                          <ProtectedRoute>
                            <ExamList />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/exam-details"
                        element={
                          <ProtectedRoute>
                            <ExamDetails />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/templates"
                        element={
                          <ProtectedRoute>
                            <Templates />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/leave-review"
                        element={
                          <ProtectedRoute>
                            <LeaveReview />
                          </ProtectedRoute>
                        }
                      />

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Layout>
                }
              />
            </Routes>

            {/* ✅ MODAL LIVES HERE SO IT CAN OPEN FROM NAVBAR */}
            {showHelpModal && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setShowHelpModal(false);
                }}
              >
                <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-md p-6 sm:p-8">
                  {!isSent ? (
                    <>
                      <h6 className="text-[20px] font-semibold text-gray-900">
                        Report a problem
                      </h6>
                      <p className="text-gray-600 mt-[7px]">
                        Found a bug or something that doesn’t look right? Let us know. Your feedback helps us improve Prep2Pass.
                      </p>

                      <textarea
                        className="w-full mt-5 p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#005FCC]"
                        rows={4}
                        placeholder="Describe what happened. You don't need to be perfect."
                        value={reportText}
                        onChange={(e) => setReportText(e.target.value)}
                      />

                      <div className="flex justify-end gap-4 mt-8">
                        <button
                      onClick={() => setShowHelpModal(false)}
                      className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-150"
                    >
                      Cancel
                    </button>
                    
                    <button
                      onClick={handleSendReport}
                      disabled={isSending}
                      className={`px-6 py-2 rounded-lg font-semibold text-white transition-colors duration-150 ${
                        isSending
                          ? "cursor-not-allowed opacity-60 bg-[#005FCC]"
                          : "bg-[#005FCC] hover:bg-[#0049A8]"
                      }`}
                    >
                      {isSending ? "Sending..." : "Send report"}
                    </button>

                      </div>
                    </>
                  ) : (
                    <>
                      <h6 className="text-[20px] font-semibold text-gray-900">Thank you</h6>
                      <p className="text-gray-600 mt-[7px]">
                        Your report has been sent. We really appreciate your help.
                      </p>

                      <div className="flex justify-end mt-8">
                        <button
                          onClick={() => {
                            setIsSent(false);
                            setShowHelpModal(false);
                          }}
                          className="px-6 py-2 rounded-lg font-semibold text-white transition-colors duration-150 bg-[#005FCC] hover:bg-[#0049A8]"
                        >
                          Close
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
